import type { FC, ReactElement } from "react";
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Gauge, SparkLineChart } from "@mui/x-charts";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  formatUsdPrecise,
  type ChartColorKey,
  type MetricCardKey,
  type MetricCardModel,
} from "../model";
import { TrendChip } from "./TrendChip";

const CARD_LOOK: Record<
  MetricCardKey,
  { icon: ReactElement; color: ChartColorKey }
> = {
  sales: { icon: <ShoppingCartOutlinedIcon />, color: "primary" },
  revenueShare: { icon: <PercentOutlinedIcon />, color: "warning" },
  paidInvoices: { icon: <PaymentsOutlinedIcon />, color: "success" },
  expenses: { icon: <ReceiptLongOutlinedIcon />, color: "error" },
  netIncome: { icon: <AccountBalanceWalletOutlinedIcon />, color: "info" },
  unpaid: { icon: <HourglassBottomOutlinedIcon />, color: "warning" },
  licenses: { icon: <WorkspacePremiumOutlinedIcon />, color: "primary" },
  clients: { icon: <GroupsOutlinedIcon />, color: "info" },
};

const DETAIL_COLOR = {
  error: "error.main",
  warning: "warning.main",
} as const;

/**
 * Карточка показателя (спека §3.2, §3.7): иконка в Avatar, крупное
 * значение, тренд-чип, спарклайн динамики или гейдж маржи, детализация
 * под Divider. Свой компонент админки: слои FSD не дают брать
 * партнёрский MetricCard.
 */
export const DashboardMetricCard: FC<{ card: MetricCardModel }> = ({
  card,
}) => {
  const theme = useTheme();
  const look = CARD_LOOK[card.key];

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: `${look.color}.main`, width: 36, height: 36 }}>
            {look.icon}
          </Avatar>
        }
        title={card.label}
        slotProps={{ title: { variant: "subtitle2", color: "text.secondary" } }}
        action={<TrendChip trend={card.trend} tone={card.tone} />}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography
            variant="h5"
            component="p"
            sx={{ fontWeight: 600 }}
            color={card.negative ? "error.main" : "text.primary"}
          >
            {card.value}
          </Typography>
          {card.gauge && (
            <Gauge
              width={72}
              height={72}
              value={card.gauge.value}
              startAngle={-110}
              endAngle={110}
              innerRadius="75%"
              text={card.gauge.label}
            />
          )}
        </Stack>
        {card.sparkline && (
          <SparkLineChart
            data={card.sparkline}
            height={48}
            area
            curve="natural"
            showTooltip
            showHighlight
            color={theme.palette[look.color].main}
            valueFormatter={(value) =>
              value === null ? "" : formatUsdPrecise(value)
            }
          />
        )}
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>
          {card.details.map((detail) => (
            <Typography
              key={detail.text}
              variant="caption"
              color={detail.tone ? DETAIL_COLOR[detail.tone] : "text.secondary"}
            >
              {detail.text}
            </Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

/** Скелетон той же формы, что карточка. */
export const DashboardMetricCardSkeleton: FC = () => (
  <Card sx={{ height: "100%" }} data-testid="metric-card-skeleton">
    <CardHeader
      avatar={<Skeleton variant="circular" width={36} height={36} />}
      title={<Skeleton width="50%" />}
      sx={{ pb: 0 }}
    />
    <CardContent>
      <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="60%" />
      <Skeleton variant="rounded" height={48} />
      <Divider sx={{ my: 1.5 }} />
      <Skeleton width="90%" />
      <Skeleton width="60%" />
    </CardContent>
  </Card>
);
